// ============================================================================
// ÉTATS D'INTERACTION DES COMPOSANTS PARTAGÉS — survol, appui, focus clavier.
//
// ⚠️ POURQUOI UNE FEUILLE INJECTÉE ET NON DES STYLES EN LIGNE. Un attribut `style` ne peut PAS exprimer
// `:hover`, `:active` ni `:focus-visible` — c'est une limite du DOM, pas un choix. Les composants
// partagés étant stylés en ligne (pour rester indépendants de la feuille de chaque app), ils étaient
// donc littéralement incapables d'avoir un état de survol : une interface plate, sans relief, dans les
// TROIS applications à la fois (constat utilisateur 2026-08-11).
//
// La réponse n'est pas de passer les composants en classes — ils perdraient leur autonomie — mais
// d'ajouter, une fois, une feuille minuscule qui ne fait QUE les états. Le style de base reste en
// ligne, l'état vient d'ici.
//
// ⚠️ `filter: brightness()` plutôt que des couleurs de survol codées : le composant garde sa palette,
// quelle que soit celle de l'app hôte. Une variante `dangerSoft` sur fond clair et un `primary` sur
// fond sombre s'éclaircissent tous les deux correctement, sans qu'on ait à déclarer six couleurs de
// plus par thème.
//
// ⚠️ `:focus-visible` et non `:focus` : un clic à la souris ne doit pas laisser un anneau derrière lui,
// mais une navigation au clavier DOIT montrer où l'on est. Sans cet anneau, la Console est inutilisable
// sans souris.
// ============================================================================

/** Classe posée par les composants partagés qui réagissent au pointeur. */
export const OZ_INTERACTIVE = 'oz-interactive';

/**
 * Variante pour une SURFACE CLAIRE cliquable (carte, rangée bordée) plutôt qu'une commande colorée.
 *
 * ⚠️ POURQUOI ELLE EXISTE : `brightness()` appliqué à une carte blanche à bordure pâle EFFACE la
 * bordure — le bloc paraît disparaître au survol. Constaté sur la rangée « Diffusé sur le site
 * internet » : après un clic, le pointeur reste dessus, et l'utilisateur voit le cadre s'évanouir
 * au moment même où il bascule l'interrupteur. Un voile posé PAR-DESSUS assombrit le fond sans
 * toucher à la bordure ni au texte.
 */
export const OZ_INTERACTIVE_SURFACE = 'oz-interactive-surface';

/**
 * Enveloppe d'infobulle — posée par `Tooltip` sur son ancre.
 *
 * ⚠️ ELLE EXISTE POUR UN SEUL CAS, ET C'EST LE PLUS UTILE : expliquer un contrôle **DÉSACTIVÉ**. Un
 * `<button disabled>` n'émet AUCUN événement de souris — ni `mouseover`, ni `mouseenter` — donc
 * l'enveloppe qui l'entoure n'apprend jamais que le pointeur est arrivé, et la bulle ne s'ouvre pas.
 * C'est précisément la raison d'être de ce composant (l'attribut `title` n'est pas rendu sur un contrôle
 * désactivé) : sans cette règle, il échouait là où on l'appelait à la rescousse.
 * `pointer-events: none` sur l'enfant désactivé laisse le pointeur atteindre l'enveloppe ; il ne coûte
 * rien, un contrôle désactivé n'ayant aucune interaction à perdre. Le curseur d'interdiction remonte
 * alors sur l'enveloppe, sans quoi il disparaîtrait avec les événements.
 */
/** Le pouce glissant du segmenté — nommé pour que le mouvement réduit puisse l'atteindre. */
export const OZ_SEG_THUMB = 'oz-seg-thumb';

export const OZ_TIP_ANCHOR = 'oz-tip-anchor';

/**
 * Le contrôle de la BARRE DU HAUT (`TopbarAction`) : sa bordure et son encre réagissent au survol, son fond dit
 * qu'un panneau est ouvert.
 * ⚠️ PAS `brightness()` comme les commandes colorées : sur un bouton papier à bordure pâle, l'éclaircir effacerait la
 *    bordure. La règle de la charte pour un bouton-icône est celle de `.btn-icon` — bordure et encre plus foncées.
 * ⚠️ Bordure, encre et fond vivent DANS la feuille et non en ligne : un style en ligne l'emporterait sur le survol,
 *    et c'est exactement pourquoi ce contrôle n'en avait aucun.
 */
export const OZ_TOPBAR_ACTION = 'oz-topbar-action';

const CSS = `
.${OZ_INTERACTIVE} {
  transition: filter .12s ease, box-shadow .12s ease, opacity .12s ease;
}
.${OZ_INTERACTIVE}:hover:not(:disabled):not([aria-disabled='true']) {
  filter: brightness(1.08);
}
/* L'appui ENFONCE légèrement : un survol seul ne distingue pas « je survole » de « j'ai cliqué ». */
.${OZ_INTERACTIVE}:active:not(:disabled):not([aria-disabled='true']) {
  filter: brightness(0.94);
}
.${OZ_INTERACTIVE}:focus-visible {
  outline: 2px solid var(--teal-500);
  outline-offset: 2px;
}
/* ⚠️ Aucun état sur un élément désactivé : laisser le curseur et l'éclaircissement réagir promettrait
   une action qui n'aura pas lieu. */
.${OZ_INTERACTIVE}:disabled, .${OZ_INTERACTIVE}[aria-disabled='true'] {
  filter: none;
  cursor: not-allowed;
}
.${OZ_INTERACTIVE_SURFACE} {
  transition: box-shadow .12s ease;
}
/* Le voile est une ombre INTERNE étalée : elle couvre le fond, jamais la bordure. Neutre en gris,
   elle tient sur une carte claire comme sur une carte sombre sans déclarer deux couleurs. */
.${OZ_INTERACTIVE_SURFACE}:hover:not(:disabled):not([aria-disabled='true']) {
  box-shadow: inset 0 0 0 999px rgba(127, 127, 127, .07);
}
.${OZ_INTERACTIVE_SURFACE}:active:not(:disabled):not([aria-disabled='true']) {
  box-shadow: inset 0 0 0 999px rgba(127, 127, 127, .13);
}
.${OZ_INTERACTIVE_SURFACE}:focus-visible {
  outline: 2px solid var(--teal-500);
  outline-offset: 2px;
}
.${OZ_INTERACTIVE_SURFACE}:disabled, .${OZ_INTERACTIVE_SURFACE}[aria-disabled='true'] {
  box-shadow: none;
  cursor: not-allowed;
}
.${OZ_TIP_ANCHOR} > :disabled,
.${OZ_TIP_ANCHOR} > [aria-disabled='true'] {
  pointer-events: none;
}
.${OZ_TIP_ANCHOR}:has(> :disabled),
.${OZ_TIP_ANCHOR}:has(> [aria-disabled='true']) {
  cursor: not-allowed;
}
.${OZ_TOPBAR_ACTION} {
  border: 1px solid var(--ink-200);
  background: var(--paper);
  color: var(--ink-600);
  transition: border-color .12s ease, color .12s ease, background .12s ease;
}
.${OZ_TOPBAR_ACTION}:hover:not(:disabled):not([aria-disabled='true']) {
  border-color: var(--ink-300);
  color: var(--ink-900);
}
/* Un panneau ouvert se voit sur son bouton : sinon rien ne relie le panneau au contrôle qui l'a ouvert. */
.${OZ_TOPBAR_ACTION}[aria-expanded='true'] {
  background: var(--ink-100);
  color: var(--ink-900);
}
.${OZ_TOPBAR_ACTION}:focus-visible {
  outline: 2px solid var(--teal-500);
  outline-offset: 2px;
}
.${OZ_TOPBAR_ACTION}[aria-disabled='true'] {
  color: var(--ink-400);
  cursor: not-allowed;
}
@media (prefers-reduced-motion: reduce) {
  .${OZ_INTERACTIVE} { transition: none; }
  .${OZ_TOPBAR_ACTION} { transition: none; }
  .${OZ_INTERACTIVE_SURFACE} { transition: none; }
  /* ⚠️ LE POUCE DU SEGMENTÉ GLISSE, donc il se tait aussi quand on demande le calme. Il change
     toujours de place — c'est le DÉPLACEMENT qu'on retire, jamais l'information. */
  .${OZ_SEG_THUMB} { transition: none; }
}
`;

/**
 * Installe la feuille, une seule fois par document.
 *
 * ⚠️ Appelée au CHARGEMENT DU MODULE (voir le bas du fichier) et non dans un effet React : un bouton
 * doit réagir dès son premier rendu, pas après le premier passage d'effets. La garde par identifiant
 * rend l'appel idempotent — plusieurs composants importent ce module, le rechargement à chaud aussi.
 */
export function installInteractiveStyles(): void {
  if (typeof document === 'undefined') return; // rendu serveur / test sans DOM : rien à installer
  const id = 'oz-interactive-styles';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.textContent = CSS;
  document.head.appendChild(el);
}

installInteractiveStyles();
